import { isProviderVisibleAtLocation } from "@/components/provider-select/provider-location-visibility";

import type { PublicProviderSummary } from "@/types/provider";
import type { ServiceDetail } from "@/types/service";

import {
  FIRST_AVAILABLE_PROVIDER_ID,
  type SelectedServiceInstance,
} from "@/stores/self-schedule-store";

/**
 * A set of selected service instances that will be performed by ONE provider.
 *
 * Multi-provider self-scheduling partitions the patient's selected services
 * into the minimum number of groups such that every service in a group can be
 * performed by a single provider (services are then performed back-to-back in
 * one appointment). When one provider can perform every selected service, there
 * is exactly one group and the flow collapses to today's single-provider path.
 */
export interface ProviderGroup {
  /** Stable key derived from the member instance ids (order-independent). */
  key: string;
  /** Selected service instances that share this group's provider. */
  instances: SelectedServiceInstance[];
  /** Distinct service ids represented in this group (for labelling). */
  serviceIds: string[];
  /** Distinct service display names, aligned with `serviceIds`. */
  serviceNames: string[];
  /**
   * Providers who can perform EVERY service in this group AND serve the chosen
   * location. Empty means no provider can deliver the group at this location
   * (a truly unbookable combination the caller should surface, not book).
   */
  eligibleProviderIds: string[];
  /** The resolved provider objects for `eligibleProviderIds`, same order. */
  eligibleProviders: PublicProviderSummary[];
}

function serviceProviderIds(service: ServiceDetail): string[] {
  return service.effectiveProviderIds ?? service.providerIds ?? [];
}

function intersect(a: Set<string>, b: Set<string>): Set<string> {
  const result = new Set<string>();
  a.forEach((id) => {
    if (b.has(id)) result.add(id);
  });
  return result;
}

/**
 * Node budget for the exact partition search below. Selections are normally
 * tiny, but the tenant's combined-service cap has no upper bound, so a large
 * fragmented selection would otherwise enumerate set partitions on the render
 * path and freeze the patient's browser mid-selection.
 */
const MAX_PARTITION_SEARCH_NODES = 50_000;

/**
 * Partition selected service instances into the minimum number of provider
 * groups. The exact search is intentionally exhaustive because self-scheduling
 * selections are small, while a first-fit merge can introduce unnecessary
 * provider handoffs for overlapping eligibility sets. Past
 * `MAX_PARTITION_SEARCH_NODES` it degrades to that first-fit merge, which is
 * still correct — every group keeps a common provider — just not provably
 * minimal.
 */
export function computeProviderGroups(
  instances: SelectedServiceInstance[],
  services: ServiceDetail[],
  providers: PublicProviderSummary[],
  locationId: string | null,
  mode: "grouped" | "per-occurrence" = "grouped",
): ProviderGroup[] {
  const serviceById = new Map(services.map((s) => [s.id, s]));
  const providerById = new Map(providers.map((p) => [p.id, p]));

  const providerMatchesLocation = (p: PublicProviderSummary) =>
    isProviderVisibleAtLocation(p.visibleLocationIds, locationId);
  const locationProviderIds = new Set(
    providers.filter(providerMatchesLocation).map((p) => p.id),
  );

  // Providers who can perform a single instance's service at this location.
  const eligibleFor = (instance: SelectedServiceInstance): Set<string> => {
    const svc = serviceById.get(instance.id);
    const ids = svc ? serviceProviderIds(svc) : [];
    return new Set(ids.filter((id) => locationProviderIds.has(id)));
  };

  // Eligibility never changes during the search, so resolve it once per instance
  // instead of rebuilding a Set at every visited node.
  const eligibleByIndex = instances.map(eligibleFor);

  type WorkingGroup = {
    instances: SelectedServiceInstance[];
    eligible: Set<string>;
  };
  let minimumGroups: WorkingGroup[] | undefined;
  let searchNodes = 0;

  const search = (instanceIndex: number, groups: WorkingGroup[]) => {
    if (searchNodes > MAX_PARTITION_SEARCH_NODES) return;
    searchNodes += 1;
    if (instanceIndex === instances.length) {
      if (!minimumGroups || groups.length < minimumGroups.length) {
        minimumGroups = groups.map((group) => ({
          instances: [...group.instances],
          eligible: new Set(group.eligible),
        }));
      }
      return;
    }
    if (minimumGroups && groups.length >= minimumGroups.length) return;

    const instance = instances[instanceIndex];
    const eligible = eligibleByIndex[instanceIndex];

    for (const group of groups) {
      const mergedEligibility = intersect(group.eligible, eligible);
      if (mergedEligibility.size === 0) continue;

      const previousEligibility = group.eligible;
      group.instances.push(instance);
      group.eligible = mergedEligibility;
      search(instanceIndex + 1, groups);
      group.eligible = previousEligibility;
      group.instances.pop();
    }

    groups.push({ instances: [instance], eligible });
    search(instanceIndex + 1, groups);
    groups.pop();
  };

  if (mode === "per-occurrence") {
    minimumGroups = instances.map((instance, index) => ({
      instances: [instance],
      eligible: eligibleByIndex[index],
    }));
  } else {
    search(0, []);
  }

  // Only reached when the budget ran out before any complete partition was
  // recorded: place each instance in the first group it shares a provider with.
  const firstFitGroups = (): WorkingGroup[] => {
    const groups: WorkingGroup[] = [];
    instances.forEach((instance, index) => {
      const eligible = eligibleByIndex[index];
      const target = groups.find(
        (group) => intersect(group.eligible, eligible).size > 0,
      );
      if (target) {
        target.eligible = intersect(target.eligible, eligible);
        target.instances.push(instance);
        return;
      }
      groups.push({ instances: [instance], eligible });
    });
    return groups;
  };

  return (minimumGroups ?? firstFitGroups()).map((group) => {
    // Keep the front page's provider order (which carries any clinic-specific
    // ordering) rather than the per-service id lists, which are sorted by id.
    const eligibleProviderIds = providers
      .filter((p) => group.eligible.has(p.id))
      .map((p) => p.id);
    const serviceIds = Array.from(
      new Set(group.instances.map((instance) => instance.id)),
    );
    return {
      key: group.instances
        .map((i) => i.instanceId)
        .sort()
        .join("|"),
      instances: group.instances,
      serviceIds,
      serviceNames: serviceIds.map((id) => serviceById.get(id)?.name ?? id),
      eligibleProviderIds,
      eligibleProviders: eligibleProviderIds
        .map((id) => providerById.get(id))
        .filter((p): p is PublicProviderSummary => Boolean(p)),
    };
  });
}

/**
 * True when the selected services cannot all be performed by a single provider
 * and therefore require a per-group provider selection.
 */
export function requiresMultipleProviders(groups: ProviderGroup[]): boolean {
  return groups.length > 1;
}

interface ReconciledProviderPreferences {
  selectedProviderId: string | null;
  serviceInstanceProviderIds: Record<string, string>;
  droppedSelection: boolean;
}

/**
 * Reconcile persisted provider preferences against the latest backend-derived
 * eligibility. Truthy local IDs are not authoritative: a provider removed
 * from the available list or from an instance's group must be dropped.
 *
 * Materialized slot assignments intentionally are not handled here. They have
 * a separate secure-booking lifecycle and changing that behavior is outside
 * preference reconciliation.
 */
export function reconcileProviderPreferences(
  groups: ProviderGroup[],
  selectedProviderId: string | null,
  serviceInstanceProviderIds: Record<string, string>,
): ReconciledProviderPreferences {
  const isFirstAvailable = (providerId: string | null | undefined) =>
    providerId === FIRST_AVAILABLE_PROVIDER_ID;
  const singleGroup = groups.length === 1 ? groups[0] : undefined;
  const reconciledSelectedProviderId =
    isFirstAvailable(selectedProviderId) ||
    (selectedProviderId !== null &&
      singleGroup?.eligibleProviderIds.includes(selectedProviderId))
      ? selectedProviderId
      : null;
  const reconciledInstanceProviders: Record<string, string> = {};

  for (const group of groups) {
    for (const instance of group.instances) {
      const providerId = serviceInstanceProviderIds[instance.instanceId];
      if (
        isFirstAvailable(providerId) ||
        group.eligibleProviderIds.includes(providerId)
      ) {
        reconciledInstanceProviders[instance.instanceId] = providerId;
      }
    }
  }

  const previousEntries = Object.entries(serviceInstanceProviderIds);
  const droppedInstanceSelection =
    previousEntries.length !==
      Object.keys(reconciledInstanceProviders).length ||
    previousEntries.some(
      ([instanceId, providerId]) =>
        reconciledInstanceProviders[instanceId] !== providerId,
    );

  return {
    selectedProviderId: reconciledSelectedProviderId,
    serviceInstanceProviderIds: reconciledInstanceProviders,
    droppedSelection:
      reconciledSelectedProviderId !== selectedProviderId ||
      droppedInstanceSelection,
  };
}
